import { useEffect, useRef } from "react"
import { useSetRecoilState } from "recoil"
import { pipParamsSelector } from "../../recoil/atoms/pictureInPicture"
import { getMainCameraElement } from "../../util/mainCamera"
import { getPlayerWP } from "../../util/getPlayerWP"
import { ConsoleLogger } from "../../util/logger"
import { productOverlaySelector } from "../../recoil/atoms/product-overlay"
import { ElementId } from "../../constants/enums"
const logger = new ConsoleLogger()

declare global {
  interface Document {
    pictureInPictureEnabled: boolean
    pictureInPictureElement: HTMLVideoElement
    exitPictureInPicture: () => void
  }
  interface HTMLVideoElement {
    requestPictureInPicture: () => Promise<unknown>
    disablePictureInPicture: boolean
  }
}

export const usePictureInPicture = () => {
  const setPiPParams = useSetRecoilState(pipParamsSelector)
  const setProductOverlay = useSetRecoilState(productOverlaySelector)

  const videoPiP = document.getElementById(ElementId.PIP_VIDEO) as HTMLVideoElement
  const currentVideoTrack = useRef<MediaStreamTrack>(null)

  useEffect(() => {
    if (
      getMainCameraElement() &&
      !getMainCameraElement().disablePictureInPicture
    ) {
      getMainCameraElement().disablePictureInPicture = true
    }
  })

  const openExternalUrl = (url: string) => {
    const targetWindow: Window = window.open(url, "_blank")
    if (!document.pictureInPictureElement) {
      exitPictureInPicture()
      setPiPParams({ isActivePiP: false })
    }
    if (targetWindow) {
      setPiPParams({ isRedirected: true })
    }

    return targetWindow
  }

  const injectStreamToVideo = (videoTrack: MediaStreamTrack) => {
    const _videoPiP = videoPiP ?? document.getElementById(ElementId.PIP_VIDEO) as HTMLVideoElement
    if (!videoTrack || !_videoPiP) return

    const stream: MediaStream = new MediaStream()
    stream.addTrack(videoTrack)
    _videoPiP.srcObject = stream
    const playPromise = _videoPiP.play()

    if (playPromise !== undefined) {
      playPromise.catch(() => _videoPiP.play())
    }
  }

  const initStream = (videoTrack: MediaStreamTrack) => {
    if (currentVideoTrack.current?.id === videoTrack?.id) return

    if (videoTrack?.readyState === "live") {
      injectStreamToVideo(videoTrack)
      currentVideoTrack.current = videoTrack
    }
  }

  const enterPictureInPicture = async () => {
    if (!document.pictureInPictureEnabled) return
    if (!document.pictureInPictureElement) {
      try {
        setTimeout(() => {
          videoPiP
            .requestPictureInPicture()
            .then(() => {})
            .catch(logger.error)
        }, 500)
      } catch (err) {
        logger.error(err, "no ready")
      }
    }
  }

  const leavePictureinPicture = () => {
    setProductOverlay({ isOpen: false })
    if (!videoPiP) return

    setPiPParams({ isActivePiP: false })
  }

  const exitPictureInPicture = async () => {
    if (!document.pictureInPictureElement) return
    try {
      await document.exitPictureInPicture()
    } catch (err) {
      logger.error("error", err)
    }
  }

  const enterWPPictureInPicture = () => {
    const videoWPPiP = getPlayerWP()
    if (videoWPPiP !== document.pictureInPictureElement) {
      try {
        setTimeout(() => {
          videoWPPiP
            .requestPictureInPicture()
            .then(() => {})
            .catch(logger.error)
        }, 500)
      } catch (err) {
        logger.error(err, "no ready")
      }
    }
  }

  const requestPictureInPictureWP = () => {
    if (getPlayerWP()) {
      enterWPPictureInPicture()
    } else {
      exitPictureInPicture()
    }
  }

  const requestPictureInPicture = () => {
    if (getMainCameraElement()) {
      enterPictureInPicture()
    } else {
      exitPictureInPicture()
    }
  }

  return {
    initStream,
    requestPictureInPicture,
    requestPictureInPictureWP,
    exitPictureInPicture,
    openExternalUrl,
    videoPiP,
    leavePictureinPicture
  } as const
}
